//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { IProduct, IProductBasic } from '@models/IProduct';
import { Mixins } from '@theme/mixins';

interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  product: IProductBasic | IProduct;
  index?: number;
}

type Props = IOwnProps;

/**
 * The Product atom renders a product in a horizontal
 * container. It displays the product image, name, price
 * and index if provided. We add 1 to the index value because
 * we assume that the indices start at zero when rendering
 * multiple products.
 */
export const Product: React.FunctionComponent<Props> = ({
  className,
  product,
  index,
}) => {
  function isProductBasic(p: IProduct | IProductBasic): p is IProductBasic {
    return (
      (p as IProductBasic).images !== undefined &&
      (p as IProductBasic).title !== undefined
    );
  }

  return (
    <ProductWrapper className={className}>
      {typeof index === 'number' && <ProductIndex>{index + 1}</ProductIndex>}
      <ProductImageContainer>
        <ProductImage
          src={
            isProductBasic(product)
              ? product.images?.[0]?.url
              : product.imageUrl
          }
        />
      </ProductImageContainer>
      <ProductDetailsContainer>
        <ProductDetail
          title={isProductBasic(product) ? product.title : product.name}
        >
          {isProductBasic(product) ? product.title : product.name}
        </ProductDetail>
        <ProductDetailPrice>{product.price}</ProductDetailPrice>
      </ProductDetailsContainer>
    </ProductWrapper>
  );
};

const ProductWrapper = styled.div(
  ({ theme }) => `
  overflow: hidden;
  flex-direction: row;
  height: 100%;
  display: flex;
  background: ${theme.colors.white};
  position: relative;
  border: solid 1px ${theme.colors.gray2};
  border-radius: ${theme.borderRadii[2]};
`,
);

const ProductImageContainer = styled.div(
  ({ theme }) => `
  flex: 0 0 ${theme.spacings[7]};
  overflow: hidden;
  position: relative;
  background: ${theme.colors.white};
  justify-content: center;
  align-items: center;
  display: flex;
`,
);

const ProductIndex = styled.div(
  ({ theme }) => `
  ${Mixins.Text.BodySecondary}
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.spacings[4]};
  height: ${theme.spacings[4]};
  border: solid 1px ${theme.colors.gray2};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: white;
  border-top: none;
  border-left: none;
`,
);

const ProductImage = styled.img`
  width: 100%;
`;

const ProductDetailsContainer = styled.div`
  padding: ${({ theme }) => theme.spacings[2]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
`;

const ProductDetail = styled.div`
  && {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
  }
`;

const ProductDetailPrice = styled(ProductDetail)`
  ${Mixins.Text.BodySecondary}
`;

export default Product;
